import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';
import Logo from './Logo';
import { fileAPI } from '../lib/api';
import { useInvestorNetworkSidebar } from '../lib/investorNetworkSidebar';
import { INVESTOR_NETWORK_NAV_ITEMS, investorNetworkTabPath } from '../lib/investorNetworkNav';

/**
 * Shared shell for investor-network dashboard tabs and sub-pages (e.g. investor detail).
 */
const InvestorNetworkLayout = ({ activeTab, tenantPath, header, children, onNotify }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { sidebarExpanded, sidebarOpen, setSidebarExpanded } = useInvestorNetworkSidebar();
    const [networkLogoUrl, setNetworkLogoUrl] = useState(null);
    const [networkLogoLoading, setNetworkLogoLoading] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoInputRef = useRef(null);

    const loadNetworkLogo = async () => {
        if (!user?.id) return;
        setNetworkLogoLoading(true);
        setNetworkLogoUrl((prev) => {
            if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev);
            return null;
        });
        try {
            const list = await fileAPI.getFilesByModule('INVESTOR_NETWORK', user.id);
            const logoFile = (list || []).find((f) => (f.documentType || f.fileCategory || '').toUpperCase() === 'LOGO');
            if (logoFile?.id) {
                const { previewUrl } = await fileAPI.getFilePreview(logoFile.id);
                setNetworkLogoUrl(previewUrl);
            }
        } catch (err) {
            console.error('Failed to load network logo:', err);
        } finally {
            setNetworkLogoLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file || !user?.id) return;
        e.target.value = '';
        if (!file.type.startsWith('image/')) {
            onNotify?.({ title: 'Invalid file', message: 'Please select an image file (e.g. PNG, JPG).', type: 'error' });
            return;
        }
        setLogoUploading(true);
        try {
            const list = await fileAPI.getFilesByModule('INVESTOR_NETWORK', user.id);
            const existingLogos = (list || []).filter((f) => (f.documentType || f.fileCategory || '').toUpperCase() === 'LOGO');
            for (const f of existingLogos) {
                try {
                    await fileAPI.deleteFile(f.id);
                } catch (_) {}
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('moduleType', 'INVESTOR_NETWORK');
            formData.append('moduleId', String(user.id));
            formData.append('fileCategory', 'LOGO');
            formData.append('visibility', 'PUBLIC');
            const res = await fetch(getApiUrl('files/upload'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.message || 'Upload failed');
            }
            await loadNetworkLogo();
            onNotify?.({ title: 'Success', message: 'Logo updated successfully.', type: 'success' });
        } catch (err) {
            onNotify?.({ title: 'Error', message: err.message || 'Failed to upload logo', type: 'error' });
        } finally {
            setLogoUploading(false);
        }
    };

    useEffect(() => {
        if (user?.id) loadNetworkLogo();
        return () => {
            setNetworkLogoUrl((prev) => {
                if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev);
                return null;
            });
        };
    }, [user?.id]);

    const navigateToTab = (tab) => {
        navigate(investorNetworkTabPath(tenantPath, tab));
    };

    return (
        <div className="min-h-screen bg-slate-50/80">
            <aside
                className={`fixed left-0 top-0 h-full z-40 flex flex-col bg-white/95 backdrop-blur-sm border-r border-slate-200/80 shadow-sm transition-[width] duration-200 ease-out ${sidebarOpen ? 'w-52' : 'w-[72px]'}`}
            >
                <div className="h-14 flex items-center shrink-0 px-3 border-b border-slate-100">
                    {sidebarOpen ? (
                        <Logo width={36} height={36} showText={true} className="[&_span]:!text-lg" />
                    ) : (
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">i</span>
                        </div>
                    )}
                </div>

                <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                    {INVESTOR_NETWORK_NAV_ITEMS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => navigateToTab(item.id)}
                            title={item.label}
                            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 ${activeTab === item.id
                                ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                        >
                            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                            </svg>
                            {sidebarOpen && <span className="text-sm truncate">{item.label}</span>}
                        </button>
                    ))}
                </nav>

                <div className="shrink-0 px-2 py-1 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={() => setSidebarExpanded((e) => !e)}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                        title={sidebarExpanded ? 'Collapse menu' : 'Expand menu'}
                    >
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={sidebarExpanded ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                        </svg>
                        {sidebarOpen && <span className="text-xs font-medium">{sidebarExpanded ? 'Collapse' : 'Expand'}</span>}
                    </button>
                </div>

                <div className="shrink-0 p-2 border-t border-slate-100 bg-slate-50/50">
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={logoUploading || networkLogoLoading}
                            className="flex-shrink-0 w-9 h-9 rounded-lg overflow-hidden bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center border border-violet-200/50 hover:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-60 transition-all"
                            title={networkLogoUrl ? 'Change logo' : 'Upload logo'}
                        >
                            {logoUploading || networkLogoLoading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : networkLogoUrl ? (
                                <img src={networkLogoUrl} alt="Network" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-white font-bold text-sm">
                                    {(user?.organizationName || user?.name || 'N')?.charAt(0)?.toUpperCase()}
                                </span>
                            )}
                        </button>
                        {sidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{user?.organizationName || user?.name}</p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                <button
                                    type="button"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={logoUploading || networkLogoLoading}
                                    className="text-xs text-violet-600 hover:text-violet-800 hover:underline disabled:opacity-60"
                                >
                                    {networkLogoUrl ? 'Change' : 'Upload'} logo
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            <main className={`transition-[margin] duration-200 ease-out ${sidebarOpen ? 'ml-52' : 'ml-[72px]'}`}>
                {header}
                {children}
            </main>
        </div>
    );
};

export default InvestorNetworkLayout;
